import { readItemField } from './item-field-reader';
import { deriveDefaultProviderNames } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { leanNestedPl } from './enchantment-template';
import { fromBaseClone, fromBaseRecord } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';

/** Native post-expansion policy; structural providers stay full, while repeated
 * ability/weapon definitions and every static size lose redundant layer data. */
export const INVENTORY_DEDUPE_ELIGIBLE_TYPES = [
    'attack_ability', 'passive_ability', 'full_ability', 'quick_ability', 'reaction_ability',
    'opportunity_attack_ability', 'free_ability', 'natural_weapon', 'spell', 'weapon_classification',
] as const;
export const INVENTORY_FORCE_SHELL_TYPES = ['size_type'] as const;
const eligible = new Set<string>(INVENTORY_DEDUPE_ELIGIBLE_TYPES);
const forced = new Set<string>(INVENTORY_FORCE_SHELL_TYPES);
const has = (value: object, name: string): boolean => Object.prototype.hasOwnProperty.call(value, name);

/** Normalize a constructed projection, never construct a missing definition.
 * This window is separate from catalog interning and cannot certify those stages. */
export function normalizeInventoryDefinition(source: Row, catalog: ProviderCatalogRows): Row {
    const result = fromBaseClone(source), seen = new Set<string>();
    const providers = deriveDefaultProviderNames(catalog);
    function walk(bucket: Row | unknown[]): void {
        const names = [...new Set([...providers.filter(name => has(bucket, name)), ...Object.keys(bucket)])];
        for (const name of names) {
            const value = (bucket as Row)[name];
            if (value !== null && typeof value === 'object' && has(value, '0'))
                for (const entry of Object.values(value)) visit(entry);
            else visit(value);
        }
    }
    function visit(value: unknown): void {
        if (!fromBaseRecord(value) || !fromBaseRecord(value.items)) return;
        // Native only visits materialized nodes with a literal nested string id.
        // Wrapped identities and rich references stay intact, even though the
        // ordinary field reader can resolve them for gameplay.
        if (typeof value.items.item_id !== 'string') return;
        const id = readItemField(value, 'item_id');
        const layers = value.item_layer_types;
        if (typeof id !== 'string' || id === '' || layers === null || typeof layers !== 'object'
            || Object.keys(layers).length === 0) return;
        if (value.items.item_type !== null && typeof value.items.item_type === 'object')
            throw new TypeError('Definition normalization requires a scalar item type');
        const type = String(readItemField({ items: value.items }, 'item_type') ?? '');
        if (forced.has(type) || (seen.has(id) && eligible.has(type))) {
            delete value.item_layer_types; delete value.computed;
            return;
        }
        seen.add(id);
        walk(layers as Row);
    }
    if (result.item_layer_types !== null && typeof result.item_layer_types === 'object') {
        // The root is never shelled. Native identity spelling is case-sensitive.
        const root = fromBaseRecord(result.items) ? result.items.item_id ?? result.item_id : result.item_id;
        if (typeof root === 'string' && root !== '') seen.add(String(readItemField({ item_id: root }, 'item_id')));
        walk(result.item_layer_types as Row);
    }
    return leanNestedPl(result) as Row;
}
